export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "deep-cleaning",
    title: "Deep Cleaning",
    subtitle: "Top-to-bottom reset for your space",
    price: "Free Estimate",
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456935945-289-limpiezaprofunda.png",
    description:
      "Our most thorough service. Deep cleaning reaches the places routine tidying never touches — behind appliances, inside cabinets, along baseboards, and into grout lines. It's the right choice for a first-time clean, a seasonal refresh, or a move-in or move-out, and it leaves your home in Ontario or anywhere across the Inland Empire genuinely reset.",
    features: [
      "Inside cabinets, drawers, oven & refrigerator",
      "Baseboards, door frames, vents & light fixtures",
      "Tile, grout & hard-water buildup treatment",
      "Detailed kitchen degreasing & bathroom sanitizing",
      "Move-in / move-out deep cleans available",
      "Family- and pet-safe cleaning products",
    ],
    process: [
      { step: "Walkthrough", desc: "We tour the space to flag problem areas, surfaces, and priorities." },
      { step: "Custom Plan", desc: "You get a written scope and a flat quote before we start — no surprises." },
      { step: "Deep Clean", desc: "Our team works room by room against a detailed checklist." },
      { step: "Final Check", desc: "We walk through with you to confirm every detail meets our standard." },
    ],
  },
  {
    slug: "regular-cleaning",
    title: "Regular Cleaning",
    subtitle: "Dependable weekly & bi-weekly service",
    price: "Free Estimate",
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456936195-80-limpiezaregular.jpg",
    description:
      "Consistent, scheduled cleaning that keeps your home comfortable without you lifting a finger. We handle kitchens, bathrooms, bedrooms, living areas, floors, and high-touch surfaces on a rhythm that fits your week — weekly, bi-weekly, or monthly. Returning clients save 20% on their next service.",
    features: [
      "Kitchens, bathrooms, bedrooms & living areas",
      "Vacuuming, mopping & dusting throughout",
      "High-touch surface sanitizing",
      "Trash removal & bed making on request",
      "Weekly, bi-weekly or monthly scheduling",
      "20% discount for returning customers",
    ],
    process: [
      { step: "Quick Quote", desc: "Tell us your square footage and schedule — we quote fast." },
      { step: "Your Checklist", desc: "We build a repeatable checklist around how you actually live." },
      { step: "Recurring Visits", desc: "The same trusted team arrives on schedule, every time." },
      { step: "Stay Flexible", desc: "Adjust frequency or add rooms whenever your needs change." },
    ],
  },
  {
    slug: "maintenance-cleaning",
    title: "Maintenance Cleaning",
    subtitle: "Ongoing upkeep for homes & businesses",
    price: "Free Estimate",
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456935260-641-limpiezamantenimiento.png",
    description:
      "Maintenance cleaning keeps a space at a consistently high standard once the heavy lifting is done. Ideal for offices, rental properties, and busy households, we schedule around your hours — including evenings and Saturdays — so floors stay clean, restrooms stay stocked, and the space is always ready for whoever walks in next.",
    features: [
      "Offices, storefronts & rental properties",
      "Restroom sanitizing & restocking",
      "Breakrooms, lobbies & common areas",
      "Floor care, vacuuming & mopping",
      "Evening & Saturday scheduling available",
      "Consistent crew who learn your space",
    ],
    process: [
      { step: "Site Visit", desc: "We assess traffic, surfaces, and the areas that matter most." },
      { step: "Service Plan", desc: "A clear scope and flat rate built around your operating hours." },
      { step: "Scheduled Upkeep", desc: "Our team arrives on time and works around your business." },
      { step: "Quality Checks", desc: "Routine reviews keep standards from slipping over time." },
    ],
  },
  {
    slug: "post-construction-cleaning",
    title: "Post-Construction Cleaning",
    subtitle: "From job site to move-in ready",
    price: "Free Estimate",
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456933262-364-limpiezacontruccion.png",
    description:
      "Construction and remodels leave behind fine drywall dust that settles into every surface for weeks. We handle the full post-construction cleanup — debris removal, dust extraction, window and fixture detailing, and a final polish — so contractors can hand over the keys and homeowners can move into a space that's genuinely finished.",
    features: [
      "Drywall dust removal from every surface",
      "Construction debris & packaging haul-away",
      "Window, track, sill & fixture detailing",
      "Floor scraping, vacuuming & final polish",
      "Cabinet, closet & interior detail cleaning",
      "Contractor and homeowner scheduling",
    ],
    process: [
      { step: "Site Assessment", desc: "We review the scope, materials, and remaining trade work." },
      { step: "Debris Removal", desc: "Leftover materials and packaging are cleared out first." },
      { step: "Dust Extraction", desc: "Multiple passes on every surface — dust settles more than once." },
      { step: "Final Detail", desc: "Glass, fixtures, and floors are polished to move-in condition." },
    ],
  },
  {
    slug: "specific-area-cleaning",
    title: "Specific Area Cleaning",
    subtitle: "Focused attention where you need it",
    price: "Free Estimate",
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456933263-486-limpiezaespecifica.png",
    description:
      "Sometimes only one part of the property needs work. Specific area cleaning targets exactly that — a kitchen before hosting, a garage that's gotten away from you, a bathroom with hard-water buildup, or a single room after a project. You pay for the area that needs it, not the whole house.",
    features: [
      "Kitchen-only deep cleans",
      "Bathroom descaling & sanitizing",
      "Garage, patio & laundry room cleanouts",
      "Single-room resets after projects",
      "Appliance interiors & cabinetry",
      "Priced by area, not by whole property",
    ],
    process: [
      { step: "Tell Us the Area", desc: "Send photos or describe the space — we quote from there." },
      { step: "Scoped Quote", desc: "A flat price for that area only, agreed before we arrive." },
      { step: "Focused Clean", desc: "Full attention on one space instead of a rushed pass everywhere." },
      { step: "Your Approval", desc: "We don't finish until the area looks the way you pictured it." },
    ],
  },
  {
    slug: "junk-removal",
    title: "Junk Removal",
    subtitle: "Haul-away for clutter & debris",
    price: "Free Estimate",
    // Client-supplied photo (the only non-stock image on the site).
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456937226-931-retiradachatarra.png",
    description:
      "Clearing out furniture, appliances, yard waste, or years of accumulated clutter is heavy, awkward work. We load it, haul it, and clean the space afterward — a single service instead of renting a truck and finding somewhere to take it. Great for estate cleanouts, garage clearing, tenant turnovers, and post-remodel debris across the Inland Empire.",
    features: [
      "Furniture, appliance & mattress removal",
      "Garage, shed & storage unit cleanouts",
      "Estate & tenant turnover cleanouts",
      "Remodel & yard debris haul-away",
      "Loading and labor included",
      "Space cleaned after the haul-out",
    ],
    process: [
      { step: "Send Photos", desc: "Share what needs to go and we'll size the job and quote it." },
      { step: "Upfront Price", desc: "You approve the flat rate before anything leaves the property." },
      { step: "We Load It", desc: "Our team handles the lifting, carrying, and loading — you don't." },
      { step: "Clean Finish", desc: "We sweep and clean the space so it's ready to use again." },
    ],
  },
  {
    slug: "move-in-move-out-cleaning",
    title: "Move-In / Move-Out Cleaning",
    subtitle: "A fresh start or a clean handoff",
    price: "Free Estimate",
    image: "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/object/public/client-media/emd-cleaning-service/images/1788456933262-892-limpiezaentradaysalida.png",
    description:
      "Whether you're settling into a new place or handing over the keys, this service makes sure the space is spotless from top to bottom. We cover every room — cabinets, appliances, floors, and fixtures — so tenants, buyers, or landlords walk into a home that's completely move-in ready, and outgoing tenants leave nothing behind but clean surfaces.",
    features: [
      "Full property cleaning, room by room",
      "Inside cabinets, drawers & appliances",
      "Bathrooms deep-cleaned & sanitized",
      "Floors vacuumed, mopped & detailed",
      "Baseboards, switches & light fixtures wiped down",
      "Flexible scheduling around your move date",
    ],
    process: [
      { step: "Move Date & Details", desc: "Tell us the move date and property size — we schedule around it." },
      { step: "Full Walkthrough Quote", desc: "A flat price covering the entire property, agreed before we start." },
      { step: "Top-to-Bottom Clean", desc: "Every room, cabinet, and surface gets full attention." },
      { step: "Move-In Ready", desc: "You walk through a space that's ready for the next chapter." },
    ],
  },
  {
    slug: "carpet-cleaning",
    title: "Carpet Cleaning",
    subtitle: "Deep extraction for fresher, cleaner carpets",
    price: "Free Estimate",
    image: "/assets/images/service-carpet-cleaning.jpg",
    description:
      "Carpets trap dirt, allergens, and odors long after regular vacuuming stops helping. Our deep carpet cleaning uses hot water extraction to lift embedded dirt and stains, treat odors at the source, and leave carpets looking and smelling genuinely fresh — a service that's ideal for high-traffic homes, pet owners, and pre-move refreshes.",
    features: [
      "Deep hot water extraction cleaning",
      "Stain treatment & spot removal",
      "Odor treatment, including pet odors",
      "High-traffic area & hallway focus",
      "Furniture moved & replaced on request",
      "Safe for kids & pets once dry",
    ],
    process: [
      { step: "Room Count & Condition", desc: "Tell us the rooms and any problem stains — we quote accordingly." },
      { step: "Pre-Treatment", desc: "Heavily soiled areas and stains get treated before extraction." },
      { step: "Deep Extraction", desc: "Hot water extraction lifts dirt and odors from deep in the fibers." },
      { step: "Dry & Fresh", desc: "Carpets are left clean, fresh, and ready to use once fully dry." },
    ],
  },
];
